import { useEffect } from 'react';

/**
 * A custom hook that checks the app version and reloads the page if a newer version is available.
 * This hook is used to ensure the user is always using the latest version of the application.
 */
export const useCacheClearWithAppVersion = () => {
  const checkVersion = async () => {
    try {
      const response = await fetch('/version.txt', { cache: 'no-store' });
      if (!response.ok) return;

      const latestVersion = (await response.text())?.trim();
      const storedVersion =
        typeof localStorage !== 'undefined' ? localStorage.getItem('appVersion') : null;

      // Trim whitespace from the stored version
      const trimmedStoredVersion = storedVersion ? storedVersion?.trim() : null;

      /*
        No stored version means there is nothing to bust: this is a first visit, or a logout, which
        clears localStorage. Record it and stop.

        Reloading here used to fight logout. `onLogout` wipes localStorage — appVersion with it — so
        every load after signing out arrived with no stored version, reloaded, hit the same expired
        session, logged out again, and looped. A missing version is not a stale build.
      */
      if (!trimmedStoredVersion) {
        localStorage.setItem('appVersion', latestVersion);
        return;
      }

      // Same build as last time — nothing to do.
      if (trimmedStoredVersion === latestVersion) return;

      // A genuinely new build: take the new version, drop the caches holding the old one, reload.
      localStorage.setItem('appVersion', latestVersion);
      if ('caches' in window) {
        window.caches
          .keys()
          .then((cacheNames) => {
            return Promise.all(cacheNames.map((cache) => caches.delete(cache)));
          })
          .finally(() => {
            // Force a hard reload with cache bypass
            window.location.replace(window.location.href);
          });
      } else {
        // If Cache API is unavailable, do a normal refresh
        window.location.replace(window.location.href);
      }
    } catch (error) {
      console.error('Failed to fetch the latest version:', error);
    }
  };

  // Check immediately on mount
  useEffect(() => checkVersion(), []);

  return;
};
